import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { Icon, iconPath, type IconName } from './Icon.js';

export interface MapPin {
  lat: number;
  lng: number;
  /** `provider`: onde o prestador estava na última posição recebida (`assistencia-33`). */
  kind: 'depot' | 'vehicle' | 'destination' | 'provider';
  label: string;
  /**
   * O pino pulsa: a posição é recente e o prestador está em rota. Quem chama
   * decide — posição velha parada pulsando seria o mapa dizendo "ao vivo" sobre
   * um ponto de dez minutos atrás.
   */
  live?: boolean;
}

/** A cor de cada pino — tokens da casa, nos dois temas. */
const PIN_COLOR: Record<MapPin['kind'], string> = {
  depot: 'var(--color-chrome)',
  vehicle: 'var(--color-primaria)',
  destination: 'var(--color-sucesso)',
  provider: 'var(--color-alerta)',
};

/**
 * O ícone de cada pino (29/10/2026): a garagem na base, de onde o prestador
 * sai e para onde volta; o carro no veículo; a bandeira de chegada no
 * destino. Antes os três eram a mesma gota, e só a cor os distinguia.
 */
const PIN_ICON: Record<MapPin['kind'], IconName> = {
  depot: 'garagem',
  vehicle: 'veiculo',
  destination: 'bandeira',
  provider: 'assistencia',
};

/** A legenda dos pinos, na ordem da viagem. */
const LEGEND: { kind: MapPin['kind']; label: string }[] = [
  { kind: 'depot', label: 'Base do prestador (saída e retorno)' },
  { kind: 'vehicle', label: 'Veículo' },
  { kind: 'destination', label: 'Destino' },
  { kind: 'provider', label: 'Prestador, na última posição' },
];

/** A gota de 44×52, com a ponta embaixo, no centro: é ali que o pino crava. */
const PIN_PATH = 'M22 50s18-15.6 18-28.5a18 18 0 10-36 0C4 34.4 22 50 22 50z';

function pinIcon(kind: MapPin['kind'], live = false): L.DivIcon {
  // O traçado de 24 cabe em 20 no centro da cabeça da gota (22, 21.5).
  // O pulso é um anel no chão, onde a ponta crava (22, 50) — `.pino-pulso`.
  const pulse = live
    ? `<span class="pino-pulso" style="color:${PIN_COLOR[kind]}"></span>`
    : '';
  return L.divIcon({
    className: '',
    html: `${pulse}<svg width="44" height="52" viewBox="0 0 44 52" style="color:${PIN_COLOR[kind]};filter:drop-shadow(0 2px 3px rgb(0 0 0 / .35))"><path d="${PIN_PATH}" fill="currentColor" stroke="white" stroke-width="2"/><g transform="translate(12 11.5) scale(0.8333)"><path d="${iconPath(PIN_ICON[kind])}" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></g></svg>`,
    iconSize: [44, 52],
    iconAnchor: [22, 50],
    tooltipAnchor: [0, -44],
  });
}

/**
 * Corta o traçado onde o prestador começa a voltar: no destino, se há um, ou
 * no veículo. O Directions devolve um traçado só para a viagem inteira, mas ele
 * passa pelos pontos de parada — o mais próximo do ponto de virada é o corte.
 * Sem base no mapa não há retorno, e o traçado inteiro é ida.
 */
export function splitReturn(
  path: [number, number][],
  pins: MapPin[],
): [going: [number, number][], back: [number, number][]] {
  const turn =
    pins.find((p) => p.kind === 'destination') ?? pins.find((p) => p.kind === 'vehicle');
  if (!turn || !pins.some((p) => p.kind === 'depot')) return [path, []];
  let best = 0;
  let bestD = Infinity;
  path.forEach(([lat, lng], i) => {
    const d = (lat - turn.lat) ** 2 + (lng - turn.lng) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return [path.slice(0, best + 1), path.slice(best)];
}

/**
 * O mapa de um itinerário — ruas de verdade (OpenStreetMap, o mesmo fundo do
 * campo de Endereço) com a rota do Google desenhada por cima e os pinos da
 * base do prestador, do veículo e do destino.
 *
 * O fundo não é do Google de propósito: o mapa do Google no navegador pediria
 * a chave no navegador, e a chave é segredo da tela de Integrações (regra 12).
 * A rota, sim, é a do Google — o traçado que a API devolveu.
 *
 * A saída e o retorno do prestador são o mesmo ponto, a base: um pino só, que
 * diz as duas coisas.
 */
export function RouteMap({
  path,
  pins,
  label,
  height = 300,
  track,
}: {
  /** O traçado já decodificado, em [lat, lng]; nulo enquanto não há rota. */
  path: [number, number][] | null;
  /** O caminho que o prestador fez de verdade, pelo link (`assistencia-33`). */
  track?: [number, number][];
  pins: MapPin[];
  label: string;
  height?: number;
}) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  /* Os pinos chegam como lista nova a cada render; a chave evita reenquadrar o mapa à toa. */
  const pinsKey = JSON.stringify(pins);
  const pathKey = path
    ? `${path.length}:${path[0]?.join()}:${path[path.length - 1]?.join()}`
    : '';
  const trackKey = track?.length
    ? `${track.length}:${track[track.length - 1]?.join()}`
    : '';

  useEffect(() => {
    if (!el.current || map.current) return;
    map.current = L.map(el.current, {
      scrollWheelZoom: true,
      attributionControl: true,
    }).setView([-3.73, -38.52], 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    const group = layer.current;
    if (!m || !group) return;
    group.clearLayers();
    const bounds: L.LatLngExpression[] = [];
    if (path && path.length > 1) {
      const [going, back] = splitReturn(path, pins);
      L.polyline(path, { className: 'rota-contorno', weight: 8 }).addTo(group);
      L.polyline(going, { className: 'rota-trajeto', weight: 5 }).addTo(group);
      if (back.length > 1)
        L.polyline(back, {
          className: 'rota-retorno',
          weight: 5,
          dashArray: '10 8',
        }).addTo(group);
      bounds.push(...path);
    }
    if (track && track.length > 1) {
      L.polyline(track, { className: 'rota-rastro', weight: 4 }).addTo(group);
      bounds.push(...track);
    }
    for (const pin of pins) {
      L.marker([pin.lat, pin.lng], {
        icon: pinIcon(pin.kind, pin.live),
        title: pin.label,
      })
        .bindTooltip(pin.label, {
          permanent: true,
          direction: 'top',
          className: 'font-semibold',
        })
        .addTo(group);
      bounds.push([pin.lat, pin.lng]);
    }
    if (bounds.length === 1) m.setView(bounds[0]!, 15);
    else if (bounds.length > 1)
      m.fitBounds(L.latLngBounds(bounds), { padding: [40, 40] });
  }, [pathKey, pinsKey, trackKey]);

  const hasReturn = Boolean(
    path && path.length > 1 && pins.some((p) => p.kind === 'depot'),
  );
  /*
   * `isolate`: o Leaflet põe as camadas em `z-index` de 400 a 1000, e sem um
   * contexto próprio elas disputam com a página — a lista de sugestões do
   * endereço, aberta logo acima, ficava atrás do mapa (29/10/2026).
   */
  return (
    <div className="relative isolate">
      <div
        ref={el}
        role="img"
        aria-label={label}
        className="w-full overflow-hidden rounded-lg border border-borda"
        style={{ height }}
      />
      {pins.length > 0 && (
        <div className="pointer-events-none absolute bottom-6 left-2 z-[1000] flex flex-col gap-1 rounded-md bg-superficie/90 px-2.5 py-1.5 text-xs font-semibold text-texto shadow-md">
          {LEGEND.filter((l) => pins.some((p) => p.kind === l.kind)).map((l) => (
            <span key={l.kind} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="flex size-6 items-center justify-center rounded-full text-primaria-contraste"
                style={{ backgroundColor: PIN_COLOR[l.kind] }}
              >
                <Icon nome={PIN_ICON[l.kind]} size={16} />
              </span>
              {l.label}
            </span>
          ))}
        </div>
      )}
      {hasReturn && (
        <div className="pointer-events-none absolute right-2 bottom-6 z-[1000] flex flex-col gap-1 rounded-md bg-superficie/90 px-2.5 py-1.5 text-xs font-semibold text-texto shadow-md">
          <span className="flex items-center gap-1.5">
            <svg width="22" height="6" aria-hidden="true">
              <line
                x1="0"
                y1="3"
                x2="22"
                y2="3"
                className="rota-trajeto"
                strokeWidth="4"
              />
            </svg>
            Ida: base até{' '}
            {pins.some((p) => p.kind === 'destination') ? 'o destino' : 'o veículo'}
          </span>
          <span className="flex items-center gap-1.5">
            <svg width="22" height="6" aria-hidden="true">
              <line
                x1="0"
                y1="3"
                x2="22"
                y2="3"
                className="rota-retorno"
                strokeWidth="4"
                strokeDasharray="6 4"
              />
            </svg>
            Retorno à base
          </span>
        </div>
      )}
    </div>
  );
}
